(ns ui.completion.event
  "\"Wait for this backend work to finish, with a deadline, then continue.\"

   The frontend half of the completion-message mechanism. A backend endpoint
   that moved work off the request thread returns a `:completion/id` in its
   response envelope; dispatch `::await` with that id and the continuation runs
   once the completion message arrives - or once the deadline expires,
   whichever comes first.

   Modelled on `frontend.state.current-session`'s `wait-for-session-state!`,
   including the part that matters most: the deadline. That helper's own
   docstring records why (\"A deadline keeps the wait a latency optimisation
   instead of a liveness dependency\") after an unbounded waiter left users
   sitting on \"Processing...\" for good. There is deliberately no way to wait
   forever here - unlike `wait-for-entity-attribute-value!`, no completion
   consumer reads a value it would get wrong by continuing early; they refetch,
   which is safe to do too soon and merely wasteful.

   Everything a handler computes is pure, and the continuations are event
   vectors, which are data. The impure parts - `js/setTimeout` handles, and
   callbacks for the fn-shaped seams - live in atoms behind the effects at the
   bottom, never in app-db."
  (:require
   [common.completion :as completion]
   [re-frame.core :as rf]
   [ui.completion.model :as model]))

(def default-timeout-ms
  "Matches `current-session/session-wait-timeout-ms`. Long enough for an
   OpenSearch bulk with an instant refresh, short enough that a lost message is
   not felt as a hang."
  5000)

(defn await-completion
  "Handler for `::await`.

   Args:
   - `:completion-id` - from the response envelope's `:completion/id`. A `nil`
     id means there is nothing to wait for, so the continuation runs at once.
     That is the fail-open case, and it covers both an endpoint that emits no
     id and an executor that rejected the work.
   - `:on-complete` - event vector to dispatch when the work finishes.
   - `:on-timeout` / `:on-error` - optional overrides, both defaulting to
     `:on-complete` (see `model/continuation` for why).
   - `:timeout-ms` - optional, defaults to `default-timeout-ms`."
  [{:keys [db]} [_ {:keys [completion-id on-complete on-timeout on-error timeout-ms]}]]
  (let [wait {:completion-id completion-id
              :on-complete   on-complete
              :on-timeout    on-timeout
              :on-error      on-error}
        already-arrived (when completion-id
                          (model/logged-completion db completion-id))]
    (cond
      (nil? on-complete)
      {}

      ;; Nothing to correlate with - continue now rather than sit out a whole
      ;; deadline waiting for a message that is never coming.
      (nil? completion-id)
      {:fx [[:dispatch on-complete]]}

      ;; The push beat the reply carrying its id. The two travel over different
      ;; channels, so this ordering is normal rather than exceptional - which
      ;; is the whole reason the arrived-completion log exists.
      already-arrived
      {:fx [[:dispatch (model/continuation
                        wait
                        (model/outcome-for-status (:completion/status already-arrived)))]]}

      :else
      (let [watch-id (str (random-uuid))]
        {:db (model/put-pending db watch-id wait)
         :fx [[::start-deadline {:watch-id   watch-id
                                 :timeout-ms (or timeout-ms default-timeout-ms)}]]}))))

(rf/reg-event-fx
 ::await
 await-completion)

(defn completion-arrived
  "Handler for the pushed completion event.

   Logs the completion (so a wait registered a moment later still sees it) and
   releases every wait keyed to it. Then it dispatches the event of each standing
   listener that wants this kind of completion, see `::listen`. A completion
   nobody is waiting on is the normal case and does nothing beyond the log entry."
  [{:keys [db]} [_ payload]]
  (let [completion-id (:completion/id payload)
        outcome (model/outcome-for-status (:completion/status payload))
        [db-after fx]
        (reduce (fn [[current-db acc] watch-id]
                  (let [[next-db wait] (model/take-pending current-db watch-id)]
                    [next-db
                     (if wait
                       (into acc
                             [[::clear-deadline watch-id]
                              [:dispatch (model/continuation wait outcome)]])
                       acc)]))
                [(model/record-completion db payload) []]
                (model/pending-watch-ids-for db completion-id))
        listener-fx (mapv (fn [listener-event]
                            [:dispatch listener-event])
                          (model/listener-events db payload))
        all-fx (into fx listener-fx)]
    (cond-> {:db db-after}
      (seq all-fx)
      (assoc :fx all-fx))))

(defn listen
  "Handler for `::listen`. Register a standing listener for completions. See
   `model/put-listener`. Pair it with `::unlisten` when the screen goes away."
  [db [_ listener]]
  (model/put-listener db listener))

(rf/reg-event-db
 ::listen
 listen)

(defn unlisten
  "Handler for `::unlisten`. Remove the listener with this id."
  [db [_ listener-id]]
  (model/remove-listener db listener-id))

(rf/reg-event-db
 ::unlisten
 unlisten)

;; The backend pushes under `common.completion/event-id`, and `comms.sente`'s
;; receive loop re-dispatches any unclaimed server push as [ev-id ev-data]. The
;; id is read from the shared cljc contract rather than retyped here, so the two
;; sides cannot drift - which is exactly the exposure the "Must match backend
;; payload" comment on :sync/sync-core-entities carries.
(rf/reg-event-fx
 completion/event-id
 completion-arrived)

(defn deadline-reached
  "Handler for `::deadline-reached`. Runs the timeout continuation, unless the
   completion already claimed this wait."
  [{:keys [db]} [_ watch-id]]
  (let [[db-after wait] (model/take-pending db watch-id)]
    (cond-> {:db db-after}
      wait
      (assoc :fx [[:dispatch (model/continuation wait :timeout)]]))))

(rf/reg-event-fx
 ::deadline-reached
 deadline-reached)

(defonce ^:private deadline-handles
  (atom {}))

(rf/reg-fx
 ::start-deadline
 (fn [{:keys [watch-id timeout-ms]}]
   (let [handle (js/setTimeout
                 (fn []
                   (swap! deadline-handles dissoc watch-id)
                   (rf/dispatch [::deadline-reached watch-id]))
                 timeout-ms)]
     (swap! deadline-handles assoc watch-id handle))))

(rf/reg-fx
 ::clear-deadline
 (fn [watch-id]
   (when-let [handle (get @deadline-handles watch-id)]
     (js/clearTimeout handle)
     (swap! deadline-handles dissoc watch-id))))

(defonce ^:private callbacks
  (atom {}))

(defn await!
  "Callback-shaped entry point, for the seams that pass functions rather than
   event vectors - `ui.view.entity-creator`'s `:on-processed!`, whose contract
   is a fn given a `:close-cb!`. Prefer `::await` wherever an event vector will
   do.

   The callback is held in an atom keyed by a generated id, so what reaches
   app-db is a string and app-db stays plain data. Exactly one of the
   completion and the deadline ever fires, so the entry is always reclaimed."
  [{:keys [completion-id timeout-ms continue!]}]
  (let [callback-id (str (random-uuid))]
    (swap! callbacks assoc callback-id continue!)
    (rf/dispatch [::await {:completion-id completion-id
                           :timeout-ms    timeout-ms
                           :on-complete   [::run-callback callback-id]}])))

(rf/reg-event-fx
 ::run-callback
 (fn [_ [_ callback-id]]
   {::call-callback callback-id}))

(rf/reg-fx
 ::call-callback
 (fn [callback-id]
   (let [callback (get @callbacks callback-id)]
     (swap! callbacks dissoc callback-id)
     (when (fn? callback)
       (callback)))))
